import React, { useState, useRef, useEffect } from 'react'
import axios from 'axios'
import { useSelector } from 'react-redux'

const apiUrl = process.env.REACT_APP_API_URL

export default function SkuManager({
  variantAxes,
  setVariantAxes,
  skus,
  setSkus,
  filterAttributes,
  setFilterAttributes,
  pId,
}) {
  const { userInfo } = useSelector((state) => state.userLogin) || {}
  const fileInputRefs = useRef({})
  const [uploadingIdx, setUploadingIdx] = useState(null)
  const [newAxisName, setNewAxisName] = useState('')
  const [newAxisValue, setNewAxisValue] = useState('')
  const [selectedAxisIndex, setSelectedAxisIndex] = useState(null)
  const [newFilterName, setNewFilterName] = useState('')
  const [newFilterValue, setNewFilterValue] = useState('')
  const [editingSkuIndex, setEditingSkuIndex] = useState(null)

  // Filter templates
  const [filterTemplates, setFilterTemplates] = useState([])
  const [filterSearch, setFilterSearch] = useState('')
  const [editingFilterIdx, setEditingFilterIdx] = useState(null)
  const [editFilterName, setEditFilterName] = useState('')
  const [editFilterValue, setEditFilterValue] = useState('')

  useEffect(() => {
    axios.get(`${apiUrl}/api/filter-templates`)
      .then((res) => setFilterTemplates(res.data || []))
      .catch(() => {})
  }, [])

  // ── Variant Axes ──────────────────────────────────────────────────
  const addAxis = () => {
    if (!newAxisName.trim()) return
    const exists = variantAxes.some(
      (a) => a.name.toLowerCase() === newAxisName.trim().toLowerCase()
    )
    if (exists) return
    setVariantAxes([...variantAxes, { name: newAxisName.trim(), values: [] }])
    setNewAxisName('')
  }

  const removeAxis = (index) => {
    const updated = variantAxes.filter((_, i) => i !== index)
    setVariantAxes(updated)
  }

  const addValueToAxis = (axisIndex) => {
    if (!newAxisValue.trim()) return
    const updated = [...variantAxes]
    if (updated[axisIndex].values.includes(newAxisValue.trim())) return
    updated[axisIndex] = {
      ...updated[axisIndex],
      values: [...updated[axisIndex].values, newAxisValue.trim()],
    }
    setVariantAxes(updated)
    setNewAxisValue('')
  }

  const removeValueFromAxis = (axisIndex, valIndex) => {
    const updated = [...variantAxes]
    updated[axisIndex] = {
      ...updated[axisIndex],
      values: updated[axisIndex].values.filter((_, i) => i !== valIndex),
    }
    setVariantAxes(updated)
  }

  // ── Generate SKU matrix ───────────────────────────────────────────
  const generateSkuMatrix = () => {
    if (variantAxes.length === 0 || variantAxes.some((a) => a.values.length === 0)) return

    // Generate all combinations
    const combinations = variantAxes.reduce(
      (acc, axis) => {
        const result = []
        acc.forEach((combo) => {
          axis.values.forEach((val) => {
            result.push({ ...combo, [axis.name]: val })
          })
        })
        return result
      },
      [{}]
    )

    // Preserve existing SKU data for matching combinations
    const newSkus = combinations.map((attrs) => {
      const existing = skus.find((s) => {
        return Object.entries(attrs).every(
          ([k, v]) => (s.attributes || {})[k] === v
        )
      })
      if (existing) return { ...existing, attributes: attrs }
      return {
        sku: '',
        attributes: attrs,
        price: 0,
        wasPrice: 0,
        quantity: 0,
        images: [],
        isActive: true,
      }
    })

    setSkus(newSkus)
  }

  // ── SKU editing ───────────────────────────────────────────────────
  const updateSkuField = (index, field, value) => {
    const updated = [...skus]
    if (field === 'price' || field === 'wasPrice' || field === 'quantity') {
      updated[index] = { ...updated[index], [field]: Number(value) || 0 }
    } else if (field === 'isActive') {
      updated[index] = { ...updated[index], [field]: value }
    } else {
      updated[index] = { ...updated[index], [field]: value }
    }
    setSkus(updated)
  }

  const removeSku = (index) => {
    setSkus(skus.filter((_, i) => i !== index))
  }

  // ── Add single SKU manually ───────────────────────────────────────
  const [addingManualSku, setAddingManualSku] = useState(false)
  const [manualSkuAttrs, setManualSkuAttrs] = useState({})

  const initManualSku = () => {
    const initial = {}
    variantAxes.forEach((a) => { initial[a.name] = a.values[0] || '' })
    setManualSkuAttrs(initial)
    setAddingManualSku(true)
  }

  const confirmManualSku = () => {
    // Check if this combo already exists
    const exists = skus.some((s) =>
      Object.entries(manualSkuAttrs).every(([k, v]) => (s.attributes || {})[k] === v)
    )
    if (exists) return
    setSkus([...skus, {
      sku: '',
      attributes: { ...manualSkuAttrs },
      price: 0,
      wasPrice: 0,
      quantity: 0,
      images: [],
      isActive: true,
    }])
    setAddingManualSku(false)
  }

  // ── SKU image upload ──────────────────────────────────────────────
  const handleSkuImageUpload = async (skuIndex, e) => {
    const files = e.target.files
    if (!files || files.length === 0) return
    if (!pId) return

    setUploadingIdx(skuIndex)
    const formData = new FormData()
    formData.append('productObjectId', pId)
    for (let i = 0; i < files.length; i++) {
      formData.append('image', files[i])
    }

    try {
      const res = await axios.post(`${apiUrl}/api/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${userInfo?.token}`,
        },
      })
      const paths = res.data.split(',').map((p) => p.trim()).filter(Boolean)
      const updated = [...skus]
      updated[skuIndex] = {
        ...updated[skuIndex],
        images: [...(updated[skuIndex].images || []), ...paths],
      }
      setSkus(updated)
    } catch (err) {
      console.error('SKU image upload failed:', err)
    }
    setUploadingIdx(null)
    // Reset the file input
    if (fileInputRefs.current[skuIndex]) {
      fileInputRefs.current[skuIndex].value = ''
    }
  }

  const removeSkuImage = (skuIndex, imgIndex) => {
    const updated = [...skus]
    updated[skuIndex] = {
      ...updated[skuIndex],
      images: updated[skuIndex].images.filter((_, i) => i !== imgIndex),
    }
    setSkus(updated)
  }

  // Drag-to-reorder images within a SKU
  const [dragState, setDragState] = useState(null)

  const handleImgDragStart = (skuIndex, imgIndex) => {
    setDragState({ skuIndex, imgIndex })
  }

  const handleImgDragOver = (e) => {
    e.preventDefault()
  }

  const handleImgDrop = (skuIndex, targetIndex) => {
    if (!dragState || dragState.skuIndex !== skuIndex) {
      setDragState(null)
      return
    }
    const fromIndex = dragState.imgIndex
    if (fromIndex === targetIndex) { setDragState(null); return }
    const updated = [...skus]
    const imgs = [...updated[skuIndex].images]
    const [moved] = imgs.splice(fromIndex, 1)
    imgs.splice(targetIndex, 0, moved)
    updated[skuIndex] = { ...updated[skuIndex], images: imgs }
    setSkus(updated)
    setDragState(null)
  }

  // ── Filter Attributes ─────────────────────────────────────────────
  const addFilter = () => {
    if (!newFilterName.trim() || !newFilterValue.trim()) return
    const name = newFilterName.trim()
    // Save as template if new
    const nameExists = filterTemplates.some(
      (t) => t.name.toLowerCase() === name.toLowerCase()
    )
    if (!nameExists) {
      axios.post(`${apiUrl}/api/filter-templates`, { name }, {
        headers: { Authorization: `Bearer ${userInfo?.token}` },
      })
        .then((res) => setFilterTemplates((prev) => [...prev, res.data].sort((a, b) => a.name.localeCompare(b.name))))
        .catch(() => {})
    }
    // Split comma-separated values into individual entries
    const values = newFilterValue.split(',').map((v) => v.trim()).filter(Boolean)
    const newEntries = values
      .filter((v) => !filterAttributes.some((f) => f.name === name && f.value === v))
      .map((v) => ({ name, value: v }))
    if (newEntries.length > 0) {
      setFilterAttributes([...filterAttributes, ...newEntries])
    }
    setNewFilterName('')
    setNewFilterValue('')
  }

  const removeFilter = (index) => {
    setFilterAttributes(filterAttributes.filter((_, i) => i !== index))
  }

  const startEditFilter = (index) => {
    setEditingFilterIdx(index)
    setEditFilterName(filterAttributes[index].name)
    setEditFilterValue(filterAttributes[index].value)
  }

  const saveEditFilter = () => {
    if (editingFilterIdx === null) return
    if (!editFilterName.trim() || !editFilterValue.trim()) return
    const updated = [...filterAttributes]
    updated[editingFilterIdx] = { name: editFilterName.trim(), value: editFilterValue.trim() }
    setFilterAttributes(updated)
    setEditingFilterIdx(null)
  }

  const filteredTemplates = filterTemplates.filter(
    (t) => t.name.toLowerCase().includes(filterSearch.toLowerCase())
  )

  // Rename all entries of a filter group
  const [editingGroupName, setEditingGroupName] = useState(null)
  const [editGroupNewName, setEditGroupNewName] = useState('')

  const renameFilterGroup = (oldName) => {
    if (!editGroupNewName.trim() || editGroupNewName.trim() === oldName) {
      setEditingGroupName(null)
      return
    }
    const updated = filterAttributes.map((f) =>
      f.name === oldName ? { ...f, name: editGroupNewName.trim() } : f
    )
    setFilterAttributes(updated)
    setEditingGroupName(null)
  }

  // Group filter attributes by name for tag-style display
  const groupedFilters = filterAttributes.reduce((acc, f, i) => {
    if (!acc[f.name]) acc[f.name] = []
    acc[f.name].push({ value: f.value, index: i })
    return acc
  }, {})

  return (
    <div>
      {/* ── VARIANT AXES ─────────────────────────────────────────── */}
      <div className="card mb-3">
        <div className="card-header">
          <strong>Variant Axes</strong>
          <small className="text-muted ms-2">
            Define what varies between SKUs (e.g. Color, Ram, Storage)
          </small>
        </div>
        <div className="card-body">
          {variantAxes.map((axis, ai) => (
            <div key={ai} className="mb-3 p-3" style={{ background: '#f8f9fa', borderRadius: 8 }}>
              <div className="d-flex align-items-center justify-content-between mb-2">
                <strong>{axis.name}</strong>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => removeAxis(ai)}
                >
                  Remove Axis
                </button>
              </div>
              <div className="d-flex flex-wrap gap-2 mb-2">
                {axis.values.map((val, vi) => (
                  <span key={vi} className="badge bg-primary d-flex align-items-center gap-1" style={{ fontSize: 14, padding: '6px 12px' }}>
                    {val}
                    <button
                      type="button"
                      className="btn-close btn-close-white ms-1"
                      style={{ fontSize: 8 }}
                      onClick={() => removeValueFromAxis(ai, vi)}
                    />
                  </span>
                ))}
              </div>
              {selectedAxisIndex === ai ? (
                <div className="d-flex gap-2">
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder={`Add ${axis.name} value...`}
                    value={newAxisValue}
                    onChange={(e) => setNewAxisValue(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addValueToAxis(ai))}
                  />
                  <button type="button" className="btn btn-sm btn-primary" onClick={() => addValueToAxis(ai)}>
                    Add
                  </button>
                  <button type="button" className="btn btn-sm btn-secondary" onClick={() => setSelectedAxisIndex(null)}>
                    Done
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => { setSelectedAxisIndex(ai); setNewAxisValue(''); }}
                >
                  + Add Value
                </button>
              )}
            </div>
          ))}

          <div className="d-flex gap-2 mt-2">
            <input
              type="text"
              className="form-control form-control-sm"
              style={{ maxWidth: 200 }}
              placeholder="New axis name..."
              value={newAxisName}
              onChange={(e) => setNewAxisName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addAxis())}
            />
            <button type="button" className="btn btn-sm btn-success" onClick={addAxis}>
              + Add Axis
            </button>
          </div>

          {variantAxes.length > 0 && variantAxes.every((a) => a.values.length > 0) && (
            <div className="mt-3">
              <button type="button" className="btn btn-warning" onClick={generateSkuMatrix}>
                Generate SKU Matrix ({variantAxes.reduce((n, a) => n * a.values.length, 1)} combinations)
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── SKU MATRIX ───────────────────────────────────────────── */}
      {skus.length > 0 && (
        <div className="card mb-3">
          <div className="card-header">
            <strong>SKU Matrix</strong>
            <span className="badge bg-info ms-2">{skus.length} SKUs</span>
          </div>
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-sm table-bordered mb-0">
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    {variantAxes.map((a) => (
                      <th key={a.name}>{a.name}</th>
                    ))}
                    <th>Price</th>
                    <th>Was Price</th>
                    <th>Qty</th>
                    <th>Images</th>
                    <th>Active</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {skus.map((sku, si) => (
                    <tr key={si}>
                      <td>{si + 1}</td>
                      {variantAxes.map((a) => (
                        <td key={a.name}>
                          <small>{(sku.attributes || {})[a.name] || '-'}</small>
                        </td>
                      ))}
                      <td>
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          style={{ width: 90 }}
                          value={sku.price || ''}
                          onChange={(e) => updateSkuField(si, 'price', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          style={{ width: 90 }}
                          value={sku.wasPrice || ''}
                          onChange={(e) => updateSkuField(si, 'wasPrice', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          style={{ width: 70 }}
                          value={sku.quantity || ''}
                          onChange={(e) => updateSkuField(si, 'quantity', e.target.value)}
                        />
                      </td>
                      <td style={{ minWidth: 180 }}>
                        {(sku.images || []).length > 0 && (
                          <div className="d-flex flex-wrap gap-1 mb-1">
                            {sku.images.map((img, ii) => (
                              <div
                                key={ii}
                                draggable
                                onDragStart={() => handleImgDragStart(si, ii)}
                                onDragOver={handleImgDragOver}
                                onDrop={() => handleImgDrop(si, ii)}
                                style={{
                                  position: 'relative', width: 40, height: 40, cursor: 'grab',
                                  opacity: dragState?.skuIndex === si && dragState?.imgIndex === ii ? 0.4 : 1,
                                  border: dragState?.skuIndex === si && dragState?.imgIndex !== ii ? '2px dashed #007bff' : 'none',
                                  borderRadius: 4,
                                }}
                              >
                                <img
                                  src={`${apiUrl}/${img}`}
                                  alt=""
                                  style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4, border: '1px solid #ddd', pointerEvents: 'none' }}
                                  onError={(e) => { e.target.style.display = 'none' }}
                                />
                                <button
                                  type="button"
                                  onClick={() => removeSkuImage(si, ii)}
                                  style={{
                                    position: 'absolute', top: -4, right: -4,
                                    width: 16, height: 16, borderRadius: '50%',
                                    background: '#dc3545', color: '#fff', border: 'none',
                                    fontSize: 10, lineHeight: '16px', padding: 0, cursor: 'pointer'
                                  }}
                                >×</button>
                              </div>
                            ))}
                          </div>
                        )}
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          ref={(el) => (fileInputRefs.current[si] = el)}
                          style={{ display: 'none' }}
                          onChange={(e) => handleSkuImageUpload(si, e)}
                        />
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          disabled={uploadingIdx === si || !pId}
                          onClick={() => fileInputRefs.current[si]?.click()}
                        >
                          {uploadingIdx === si ? '...' : '+ Upload'}
                        </button>
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          checked={sku.isActive !== false}
                          onChange={(e) => updateSkuField(si, 'isActive', e.target.checked)}
                        />
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => removeSku(si)}
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          {variantAxes.length > 0 && (
            <div className="card-footer">
              {!addingManualSku ? (
                <button type="button" className="btn btn-sm btn-outline-primary" onClick={initManualSku}>
                  + Add SKU
                </button>
              ) : (
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  {variantAxes.map((a) => (
                    <div key={a.name}>
                      <label className="form-label mb-0 me-1" style={{ fontSize: 12 }}>{a.name}:</label>
                      <select
                        className="form-select form-select-sm d-inline-block"
                        style={{ width: 'auto' }}
                        value={manualSkuAttrs[a.name] || ''}
                        onChange={(e) => setManualSkuAttrs({ ...manualSkuAttrs, [a.name]: e.target.value })}
                      >
                        {a.values.map((v) => (
                          <option key={v} value={v}>{v}</option>
                        ))}
                      </select>
                    </div>
                  ))}
                  <button type="button" className="btn btn-sm btn-success" onClick={confirmManualSku}>Add</button>
                  <button type="button" className="btn btn-sm btn-secondary" onClick={() => setAddingManualSku(false)}>Cancel</button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── FILTER ATTRIBUTES ────────────────────────────────────── */}
      <div className="card mb-3">
        <div className="card-header">
          <strong>Filter Attributes</strong>
          <small className="text-muted ms-2">
            Used for category page filtering (e.g. Os, Battery, Screen Size)
          </small>
        </div>
        <div className="card-body">
          {Object.keys(groupedFilters).length > 0 && (
            <div className="mb-3">
              {Object.entries(groupedFilters).map(([name, entries]) => (
                <div key={name} className="d-flex align-items-start gap-2 mb-2 p-2" style={{ background: '#f8f9fa', borderRadius: 6 }}>
                  {editingGroupName === name ? (
                    <div className="d-flex gap-1" style={{ minWidth: 120 }}>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        style={{ width: 120 }}
                        value={editGroupNewName}
                        onChange={(e) => setEditGroupNewName(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), renameFilterGroup(name))}
                        autoFocus
                      />
                      <button type="button" className="btn btn-sm btn-success" onClick={() => renameFilterGroup(name)}>OK</button>
                      <button type="button" className="btn btn-sm btn-secondary" onClick={() => setEditingGroupName(null)}>Cancel</button>
                    </div>
                  ) : (
                    <strong
                      style={{ minWidth: 120, paddingTop: 4, cursor: 'pointer' }}
                      title="Click to rename"
                      onClick={() => { setEditingGroupName(name); setEditGroupNewName(name) }}
                    >{name}:</strong>
                  )}
                  <div className="d-flex flex-wrap gap-1 flex-grow-1">
                    {entries.map((entry) => (
                      editingFilterIdx === entry.index ? (
                        <div key={entry.index} className="d-flex gap-1">
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            style={{ width: 120 }}
                            value={editFilterValue}
                            onChange={(e) => setEditFilterValue(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), saveEditFilter())}
                            autoFocus
                          />
                          <button type="button" className="btn btn-sm btn-success" onClick={saveEditFilter}>Save</button>
                          <button type="button" className="btn btn-sm btn-secondary" onClick={() => setEditingFilterIdx(null)}>Cancel</button>
                        </div>
                      ) : (
                        <span key={entry.index} className="badge bg-secondary d-flex align-items-center gap-1" style={{ fontSize: 13, padding: '5px 8px', fontWeight: 400 }}>
                          {entry.value}
                          <button
                            type="button"
                            className="btn-close btn-close-white ms-1"
                            style={{ fontSize: 7 }}
                            onClick={() => removeFilter(entry.index)}
                          />
                          <button
                            type="button"
                            style={{ background: 'none', border: 'none', color: '#fff', fontSize: 10, padding: 0, cursor: 'pointer', textDecoration: 'underline' }}
                            onClick={() => startEditFilter(entry.index)}
                          >
                            edit
                          </button>
                        </span>
                      )
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mb-2" style={{ position: 'relative' }}>
            <div className="d-flex gap-2">
              <div style={{ position: 'relative', flex: '0 0 200px' }}>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Search or type filter name..."
                  value={newFilterName || filterSearch}
                  onChange={(e) => {
                    setFilterSearch(e.target.value)
                    setNewFilterName(e.target.value)
                  }}
                  onFocus={() => setFilterSearch(newFilterName)}
                  onBlur={() => setTimeout(() => setFilterSearch(''), 200)}
                />
                {filterSearch && (
                  <div className="list-group" style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10, maxHeight: 200, overflowY: 'auto', boxShadow: '0 2px 8px rgba(0,0,0,.15)' }}>
                    {filteredTemplates.map((t) => (
                      <button
                        key={t._id}
                        type="button"
                        className="list-group-item list-group-item-action py-1"
                        style={{ fontSize: 13 }}
                        onMouseDown={() => {
                          setNewFilterName(t.name)
                          setFilterSearch('')
                        }}
                      >
                        {t.name}
                      </button>
                    ))}
                    {filteredTemplates.length === 0 && (
                      <div className="list-group-item py-1 text-muted" style={{ fontSize: 13 }}>
                        New filter — will be saved as template
                      </div>
                    )}
                  </div>
                )}
              </div>
              <input
                type="text"
                className="form-control form-control-sm"
                style={{ maxWidth: 200 }}
                placeholder="Filter value"
                value={newFilterValue}
                onChange={(e) => setNewFilterValue(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addFilter())}
              />
              <button type="button" className="btn btn-sm btn-success" onClick={addFilter}>
                Add
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
